import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicLink, saveDraft, submitAnswers, ApiError } from '../api/client';
import type { AnswerValue, PublicLinkResponse, PublicQuestion } from '../types';

type SaveState = 'idle' | 'saving' | 'saved' | 'submitted' | 'error';

export default function PublicFormPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicLinkResponse | null>(null);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [notFound, setNotFound] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationIssues, setValidationIssues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!token) return;
    getPublicLink(token)
      .then((res) => {
        setData(res);
        setAnswers(res.answers ?? {});
      })
      .catch(() => setNotFound(true));
  }, [token]);

  const grouped = useMemo(() => {
    if (!data) return [];
    const byCategory = new Map<string, PublicQuestion[]>();
    for (const q of [...data.questions].sort((a, b) => a.order - b.order)) {
      const list = byCategory.get(q.category) ?? [];
      list.push(q);
      byCategory.set(q.category, list);
    }
    return Array.from(byCategory.entries());
  }, [data]);

  function setAnswer(questionId: string, value: AnswerValue) {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }

  async function handleSaveDraft() {
    if (!token) return;
    setSaveState('saving');
    setErrorMessage(null);
    try {
      await saveDraft(token, answers);
      setSaveState('saved');
    } catch {
      setSaveState('error');
      setErrorMessage('Could not save your progress. Please try again.');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setSaveState('saving');
    setErrorMessage(null);
    setValidationIssues({});
    try {
      await submitAnswers(token, answers);
      setSaveState('submitted');
    } catch (err) {
      setSaveState('error');
      if (err instanceof ApiError) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Could not submit your answers. Please try again.');
      }
    }
  }

  if (notFound) {
    return (
      <div className="public-form">
        <div className="error-banner">This link is not available.</div>
      </div>
    );
  }

  if (!data) {
    return <div className="public-form">Loading...</div>;
  }

  if (saveState === 'submitted') {
    return (
      <div className="public-form">
        <div className="card">
          <h1>Thank you</h1>
          <p>Your answers have been submitted. You may close this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-form">
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <img
          src="/phoenix-logo.png"
          alt="Phoenix Consulting"
          style={{ height: 32, width: 'auto', maxWidth: 'none', flex: '0 0 auto', objectFit: 'contain' }}
        />
        {data.logoUrl && (
          <img
            src={data.logoUrl}
            alt={`${data.companyName} logo`}
            style={{ height: 40, maxWidth: 200, objectFit: 'contain', flex: 'none' }}
          />
        )}
      </div>
      <h1 className="questionnaire-title">{data.companyName} questionnaire</h1>
      <p className="muted" style={{ textAlign: 'center' }}>
        This link expires {new Date(data.expiresAt).toLocaleDateString()}.
      </p>

      {errorMessage && <div className="error-banner">{errorMessage}</div>}

      <form onSubmit={handleSubmit}>
        {grouped.map(([category, questions]) => (
          <div key={category} className="card">
            <h2>{category}</h2>
            {questions.map((q) => (
              <QuestionField
                key={q.questionId}
                question={q}
                value={answers[q.questionId]}
                onChange={(v) => setAnswer(q.questionId, v)}
                error={validationIssues[q.questionId]}
              />
            ))}
          </div>
        ))}

        <div
          className="row"
          style={{
            position: 'sticky',
            bottom: 16,
            marginTop: 8,
            paddingTop: 8,
          }}
        >
          <button type="button" onClick={handleSaveDraft} disabled={saveState === 'saving'}>
            {saveState === 'saving' ? 'Saving...' : 'Save draft'}
          </button>
          <button type="submit" className="primary" disabled={saveState === 'saving'}>
            {saveState === 'saving' ? 'Submitting...' : 'Submit'}
          </button>
        </div>
        {saveState === 'saved' && <p className="muted">Draft saved.</p>}
      </form>
    </div>
  );
}

function QuestionField({
  question,
  value,
  onChange,
  error,
}: {
  question: PublicQuestion;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue) => void;
  error?: string;
}) {
  const fieldId = `q-${question.questionId}`;

  return (
    <div className="field">
      <label htmlFor={fieldId}>
        {question.text}
        {question.required && ' *'}
      </label>
      {question.helpText && <p className="muted" style={{ marginTop: -4 }}>{question.helpText}</p>}

      {question.type === 'short_text' && (
        <input id={fieldId} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} required={question.required} />
      )}
      {question.type === 'long_text' && (
        <textarea
          id={fieldId}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          required={question.required}
        />
      )}
      {question.type === 'number' && (
        <input
          id={fieldId}
          type="number"
          value={(value as number) ?? ''}
          onChange={(e) => onChange(Number(e.target.value))}
          required={question.required}
        />
      )}
      {question.type === 'yes_no' && (
        <select
          id={fieldId}
          value={value === undefined ? '' : value ? 'yes' : 'no'}
          onChange={(e) => onChange(e.target.value === 'yes')}
          required={question.required}
        >
          <option value="" disabled>
            Select...
          </option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      )}
      {question.type === 'single_choice' && (
        <select id={fieldId} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} required={question.required}>
          <option value="" disabled>
            Select...
          </option>
          {(question.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )}
      {question.type === 'multiple_choice' && (
        <div>
          {(question.options ?? []).map((opt) => {
            const selected = Array.isArray(value) && value.includes(opt);
            return (
              <label key={opt} style={{ fontWeight: 400, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={selected}
                  onChange={(e) => {
                    const current = Array.isArray(value) ? value : [];
                    onChange(e.target.checked ? [...current, opt] : current.filter((v) => v !== opt));
                  }}
                />
                {opt}
              </label>
            );
          })}
        </div>
      )}
      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
    </div>
  );
}
